/* ============================================================
   WISHLIST REFACTOR — 27APR26
   Root causes fixed:
   1. syncToMstStorage() was writing to iwish_list BEFORE MST's click handler
      fired, so MST read the pre-modified state and inverted the action.
   2. reconcileBlocklist() cleared the blocklist when MST's server resync
      returned an item (thinking it was re-added elsewhere), causing removed
      items to reappear on every reload.
   3. Proxy-heart dispatchEvent() creates isTrusted:false events that MST's
      handler ignores — server removes on the wishlist page never persisted.
   4. aneka_wishlist (device-local) drifted from iwish_list (server-synced),
      breaking cross-device sync and producing wrong counters on login.

   New architecture:
   - MST Wishlist app owns iwish_list exclusively. We NEVER write to it
     except in the wishlist page's own remove handler (local-first UX).
   - Our click listener does optimistic UI only (toggle button class,
     update counter) and does NOT touch localStorage.
   - MST's window-level handler fires after ours and makes the API call.
   - All UI state is derived from iwish_list via getMstVariantIds().
   - Cross-tab sync: window.storage event re-reads iwish_list when another
     tab updates it. Cross-device sync happens on page load via MST's own
     server sync that populates iwish_list.
   ============================================================ */
(function () {
  const RECENTLY_VIEWED_KEY = 'aneka_recently_viewed';
  const MAX_RECENTLY_VIEWED = 8;

  function readList(key) {
    try {
      const parsed = JSON.parse(window.localStorage.getItem(key));
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      return [];
    }
  }

  function writeList(key, values) {
    window.localStorage.setItem(key, JSON.stringify(values));
  }

  function trackRecentlyViewed() {
    const productInfo = document.querySelector('product-info[data-product-handle]');

    if (!productInfo) {
      return;
    }

    const handle = productInfo.dataset.productHandle;

    if (!handle) {
      return;
    }

    const nextViewed = [handle, ...readList(RECENTLY_VIEWED_KEY).filter((item) => item !== handle)].slice(
      0,
      MAX_RECENTLY_VIEWED
    );

    writeList(RECENTLY_VIEWED_KEY, nextViewed);
  }

  async function fetchProduct(handle) {
    const response = await window.fetch(`/products/${handle}.js`);

    if (!response.ok) {
      throw new Error(`Unable to load product ${handle}`);
    }

    return response.json();
  }

  function formatMoney(cents) {
    if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function') {
      return Shopify.formatMoney(cents, window.theme?.moneyFormat);
    }

    return `₹ ${(cents / 100).toFixed(2)}`;
  }

  // Inject hover-image CSS once so recently-viewed cards show the second
  // product photo on hover, matching collection card behaviour.
  (function injectHoverImageStyles() {
    if (document.getElementById('aneka-hover-img-styles')) return;
    const style = document.createElement('style');
    style.id = 'aneka-hover-img-styles';
    style.textContent = `
      .aneka-storefront-card__media { position: relative; overflow: hidden; display: block; }
      .aneka-storefront-card__img { display: block; width: 100%; height: 100%; object-fit: cover; }
      .aneka-storefront-card__img--hover {
        position: absolute; top: 0; left: 0; width: 100%; height: 100%;
        opacity: 0; transition: opacity 0.3s ease;
      }
      .aneka-storefront-card__media:hover .aneka-storefront-card__img--hover { opacity: 1; }
    `;
    document.head.appendChild(style);
  })();

  function renderProductCard(product, options) {
    const image = product.featured_image || product.images?.[0] || '';
    // Capture second image URL for hover swap on recently-viewed cards.
    const secondImage = product.images?.[1] || '';
    const compareAtPrice = product.compare_at_price || 0;
    const showSale = compareAtPrice > product.price;
    const badge = options.badge || '';
    const compact = options.compact === true;
    const safeTitle = product.title.replace(/"/g, '&quot;');

      return `
      <article class="aneka-storefront-card">
        <a class="aneka-storefront-card__media" href="${product.url}">
          ${badge ? `<span class="aneka-storefront-card__badge">${badge}</span>` : ''}
          ${image ? `<img class="aneka-storefront-card__img aneka-storefront-card__img--primary" src="${image}" alt="${safeTitle}" loading="lazy">` : ''}
          ${secondImage ? `<img class="aneka-storefront-card__img aneka-storefront-card__img--hover" src="${secondImage}" alt="${safeTitle}" loading="lazy">` : ''}
        </a>
        <div class="aneka-storefront-card__content">
          <a class="aneka-storefront-card__title" href="${product.url}">${product.title}</a>
          ${
            compact
              ? ''
              : `<div class="aneka-storefront-card__price">
                  ${showSale ? `<span class="aneka-storefront-card__compare">${formatMoney(compareAtPrice)}</span>` : ''}
                  <span class="aneka-storefront-card__sale">${formatMoney(product.price)}</span>
                </div>
                <div class="aneka-storefront-card__actions">
                  <a class="aneka-storefront-card__cta" href="${product.url}">View product</a>
                </div>`
          }
        </div>
      </article>
    `;
  }

  async function renderRecentlyViewed() {
    // Get the section root element. The root starts with [hidden] set server-side
    // so nothing shows before JS runs.
    const root = document.querySelector('[data-recently-viewed-root]');

    if (!root) {
      return;
    }

    // Get the header element separately so we can show/hide it only when
    // products are available. This prevents the heading from appearing empty
    // on first-ever visit.
    const header = root.querySelector('[data-recently-viewed-header]');

    // Exclude the currently viewed product from the recently viewed list so
    // the PDP doesn't show itself in the rail.
    const currentHandle = document.querySelector('product-info[data-product-handle]')?.dataset.productHandle;
    const handles = readList(RECENTLY_VIEWED_KEY).filter((handle) => handle !== currentHandle).slice(0, 5);

    if (!handles.length) {
      // No previously viewed products yet. Keep the root hidden AND ensure
      // the header is hidden too so neither heading nor rail appears on first visit.
      root.setAttribute('hidden', 'hidden');
      if (header) header.setAttribute('hidden', 'hidden');
      return;
    }

    const products = await Promise.all(
      handles.map(async (handle) => {
        try {
          return await fetchProduct(handle);
        } catch (error) {
          return null;
        }
      })
    );

    // Filter out any failed fetches (null) before rendering.
    const validProducts = products.filter(Boolean);

    const list = root.querySelector('[data-recently-viewed-list]');
    list.innerHTML = validProducts
      .map((product) => renderProductCard(product, { compact: true }))
      .join('');

    if (!validProducts.length) {
      // All product fetches failed (e.g. products deleted). Keep section hidden
      // so no empty heading shows.
      root.setAttribute('hidden', 'hidden');
      if (header) header.setAttribute('hidden', 'hidden');
      return;
    }

    // Products are ready — reveal BOTH the header and the root section together
    // so heading and grid appear simultaneously with no flash of just the heading.
    root.removeAttribute('hidden');
    if (header) header.removeAttribute('hidden');
  }

  // ── WISHLIST ──────────────────────────────────────────────────────────────
  // MST's click handler is unreliable on this theme. We own the click entirely:
  // intercept it, update iwish_list localStorage ourselves, call MST's JS API
  // for server sync, and update the UI. Same pattern the wishlist page uses
  // for removes (callMstServerRemove).
  // ──────────────────────────────────────────────────────────────────────────

  var MST_WISHLIST_KEY = 'iwish_list';
  var REMOVED_KEY      = 'aneka_wl_removed';

  function getRemovedVids() {
    try {
      var r = JSON.parse(localStorage.getItem(REMOVED_KEY)) || {};
      return Array.isArray(r.vids) ? r.vids.map(String) : [];
    } catch (_) { return []; }
  }

  function addToRemovedVids(vid) {
    try {
      var vids = getRemovedVids();
      if (vids.indexOf(String(vid)) === -1) vids.push(String(vid));
      localStorage.setItem(REMOVED_KEY, JSON.stringify({ vids: vids }));
    } catch (_) {}
  }

  function clearFromRemovedVids(vid) {
    try {
      var vids = getRemovedVids().filter(function (v) { return v !== String(vid); });
      localStorage.setItem(REMOVED_KEY, JSON.stringify({ vids: vids }));
    } catch (_) {}
  }

  function getRawMstVariantIds() {
    try {
      var entries = JSON.parse(localStorage.getItem(MST_WISHLIST_KEY)) || [];
      return Array.isArray(entries)
        ? entries.map(function (e) { return String(e[0] || e); }).filter(Boolean)
        : [];
    } catch (_) { return []; }
  }

  function getMstVariantIds() {
    var removed = getRemovedVids();
    return getRawMstVariantIds().filter(function (vid) { return removed.indexOf(vid) === -1; });
  }

  function addToMstList(vid, pid) {
    try {
      var entries = JSON.parse(localStorage.getItem(MST_WISHLIST_KEY)) || [];
      if (!Array.isArray(entries)) entries = [];
      var exists = entries.some(function (e) { return String(e[0] || e) === vid; });
      if (!exists) {
        entries.push([vid, pid || '', 1]);
        localStorage.setItem(MST_WISHLIST_KEY, JSON.stringify(entries));
      }
    } catch (_) {}
  }

  function removeFromMstList(vid) {
    try {
      var entries = JSON.parse(localStorage.getItem(MST_WISHLIST_KEY)) || [];
      if (!Array.isArray(entries)) return;
      localStorage.setItem(MST_WISHLIST_KEY, JSON.stringify(
        entries.filter(function (e) { return String(e[0] || e) !== vid; })
      ));
    } catch (_) {}
  }

  function callMstAdd(vid, pid, retry) {
    try {
      if (window.iWish && typeof window.iWish.iwishAdd === 'function') {
        window.iWish.iwishAdd(vid, pid, 1);
      } else if (!retry) {
        setTimeout(function () { callMstAdd(vid, pid, true); }, 1000);
      }
    } catch (_) {}
  }

  function callMstRemove(vid, pid, retry) {
    try {
      if (window.iWish && typeof window.iWish.removeFromWishlist === 'function') {
        window.iWish.removeFromWishlist(vid, pid);
      } else if (window.iWish && typeof window.iWish.iwishAdd === 'function') {
        window.iWish.iwishAdd(vid, pid, 0);
      } else if (!retry) {
        setTimeout(function () { callMstRemove(vid, pid, true); }, 1000);
      }
    } catch (_) {}
  }

  function syncWishlistCounters() {
    var count = getMstVariantIds().length;
    var txt   = count > 0 ? String(count) : '';
    document.querySelectorAll('.iwish-counter').forEach(function (el) {
      if (el.textContent.trim() !== txt) el.textContent = txt;
    });
  }

  function syncWishlistButtonStates() {
    var vids = getMstVariantIds();
    document.querySelectorAll('.iWishAddColl.iwishcheck, .iWishAdd.st-1').forEach(function (btn) {
      var vid = String(btn.dataset.variant || '');
      if (vid) btn.classList.toggle('active', vids.indexOf(vid) !== -1);
    });
  }

  document.addEventListener('click', function (e) {
    var btn = e.target.closest('.iWishAddColl.iwishcheck, .iWishAdd.st-1');
    if (!btn) return;

    // Product page: read the currently selected variant.
    if (btn.closest('.iwish-app-block-product')) {
      var sel = document.querySelector('[name=id]');
      if (sel && sel.value) btn.dataset.variant = String(sel.value);
    }

    var vid      = String(btn.dataset.variant || '');
    var pid      = String(btn.dataset.product || '');
    var isActive = btn.classList.contains('active');

    if (!vid) return;

    // We own this click — stop MST's handler from also firing.
    e.preventDefault();
    e.stopPropagation();

    // Blocked item still on server: just unblock locally, no API call needed.
    if (!isActive && getRemovedVids().indexOf(vid) !== -1 && getRawMstVariantIds().indexOf(vid) !== -1) {
      clearFromRemovedVids(vid);
      btn.classList.add('active');
      syncWishlistCounters();
      return;
    }

    if (isActive) {
      // REMOVE
      addToRemovedVids(vid);
      removeFromMstList(vid);
      btn.classList.remove('active');
      callMstRemove(vid, pid, false);
    } else {
      // ADD
      clearFromRemovedVids(vid);
      addToMstList(vid, pid);
      btn.classList.add('active');
      callMstAdd(vid, pid, false);

      // Meta Pixel — AddToWishlist standard event
      if (typeof window.fbq === 'function') {
        var pTitle = btn.dataset.ptitle || btn.dataset.pTitle || '';
        window.fbq('track', 'AddToWishlist', {
          content_ids:  [pid],
          content_name: pTitle,
          content_type: 'product',
          currency:     'INR',
        });
      }
    }

    syncWishlistCounters();
    setTimeout(function () { syncWishlistCounters(); syncWishlistButtonStates(); }, 1500);
  }, true);

  window.addEventListener('storage', function (e) {
    if (e.key !== MST_WISHLIST_KEY) return;
    syncWishlistCounters();
    syncWishlistButtonStates();
  });

  function initCounterObserver() {
    document.querySelectorAll('.iwish-counter').forEach(function (el) {
      new MutationObserver(function () {
        if (el.textContent.trim() === '0') { el.textContent = ''; return; }
        syncWishlistButtonStates();
      }).observe(el, { childList: true, characterData: true, subtree: true });
    });
  }

  function initWishlist() {
    initCounterObserver();
    setTimeout(function () { syncWishlistCounters(); syncWishlistButtonStates(); }, 400);
    setTimeout(function () { syncWishlistCounters(); syncWishlistButtonStates(); }, 1800);
  }
  // ── END WISHLIST ──────────────────────────────────────────────────────────

  function initializeAnekaStorefront() {
    trackRecentlyViewed();
    renderRecentlyViewed();
    initWishlist();
  }

  document.addEventListener('DOMContentLoaded', initializeAnekaStorefront);
  // Re-sync wishlist state after Shopify theme editor section reloads.
  document.addEventListener('shopify:section:load', function () {
    trackRecentlyViewed();
    renderRecentlyViewed();
    syncWishlistButtonStates();
    syncWishlistCounters();
  });
})();

/* ============================================================
   Collection card click — makes the entire card navigate to
   its collection URL. Dawn's native full-width-link can be
   blocked by z-index stacking or third-party overlays; this
   handler fires on the card wrapper itself and is immune.
   ============================================================ */
(function () {
  function initCollectionCardClicks() {
    document.querySelectorAll('.collection-card-wrapper').forEach(function (wrapper) {
      if (wrapper.dataset.collectionClickInit) return;
      wrapper.dataset.collectionClickInit = '1';
      var link = wrapper.querySelector('a[href]');
      if (!link) return;
      var url = link.getAttribute('href');
      wrapper.style.cursor = 'pointer';
      wrapper.addEventListener('click', function (e) {
        if (e.target.closest('a')) return; // let real link clicks through
        window.location.href = url;
      });
    });
  }

  document.addEventListener('DOMContentLoaded', initCollectionCardClicks);
  document.addEventListener('shopify:section:load', initCollectionCardClicks);
})();

/* ============================================================
   Mobile price range slider — dual-handle logic
   Initialises the custom range slider in the mobile filter
   drawer, syncs min/max handles, updates the gold fill bar,
   shows live price display, and auto-submits the facet form
   when the user releases a handle (matching desktop behaviour).
   ============================================================ */
(function () {
  'use strict';

  // Format a raw number (paise) into ₹ display string — no decimals.
  function formatSliderMoney(value) {
    return '₹' + Number(Math.round(value)).toLocaleString('en-IN');
  }

  // Calculate the percentage position of a value within a range.
  function pct(value, min, max) {
    if (max === min) return 0;
    return ((value - min) / (max - min)) * 100;
  }

  // Update the gold fill bar between the two slider thumbs and the
  // live price display label above the slider.
  function updateSliderUI(wrapper) {
    var minInput = wrapper.querySelector('.mobile-facets__range-input--min');
    var maxInput = wrapper.querySelector('.mobile-facets__range-input--max');
    var fill = wrapper.querySelector('.mobile-facets__range-fill');
    var displayMin = wrapper.querySelector('[id^="MobileSliderMin"]');
    var displayMax = wrapper.querySelector('[id^="MobileSliderMax"]');

    if (!minInput || !maxInput || !fill) return;

    var rangeMax = parseFloat(minInput.getAttribute('data-range-max')) || parseFloat(minInput.max) || 100;
    var minVal = parseFloat(minInput.value) || 0;
    var maxVal = parseFloat(maxInput.value) || rangeMax;

    // Clamp so min never exceeds max.
    if (minVal > maxVal) {
      minVal = maxVal;
      minInput.value = minVal;
    }

    var leftPct = pct(minVal, 0, rangeMax);
    var rightPct = pct(maxVal, 0, rangeMax);

    // Position the gold fill between the two thumb positions.
    fill.style.left = leftPct + '%';
    fill.style.width = (rightPct - leftPct) + '%';

    // Update the live price labels above the slider.
    if (displayMin) displayMin.textContent = formatSliderMoney(minVal);
    if (displayMax) displayMax.textContent = formatSliderMoney(maxVal);
  }

  // Debounce helper — waits N ms after the last call before firing.
  function debounce(fn, ms) {
    var timer;
    return function () {
      clearTimeout(timer);
      var args = arguments;
      var ctx = this;
      timer = setTimeout(function () { fn.apply(ctx, args); }, ms);
    };
  }

  // Submit the mobile facet form so the filter applies automatically
  // when the slider handle is released — matching desktop auto-apply behaviour.
  function submitMobileFacetForm() {
    var form = document.getElementById('FacetFiltersFormMobile');
    if (!form) return;
    // Dispatch an input event on the form so FacetFiltersForm's existing
    // debouncedOnSubmit handler picks it up without duplicating renderPage logic.
    form.dispatchEvent(new Event('input', { bubbles: true }));
  }

  var debouncedSubmit = debounce(submitMobileFacetForm, 600);

  // Bind all slider inputs inside a given price slider wrapper.
  function bindSlider(wrapper) {
    if (wrapper.dataset.anekaSliderBound) return;
    wrapper.dataset.anekaSliderBound = 'true';

    var minInput = wrapper.querySelector('.mobile-facets__range-input--min');
    var maxInput = wrapper.querySelector('.mobile-facets__range-input--max');

    if (!minInput || !maxInput) return;

    // On every drag movement, update the fill and price labels.
    [minInput, maxInput].forEach(function (input) {
      input.addEventListener('input', function () {
        updateSliderUI(wrapper);
      });

      // On mouse/touch release, also update the hidden text inputs that the
      // price-range custom element uses for form submission, then submit.
      input.addEventListener('change', function () {
        updateSliderUI(wrapper);

        // Sync values into the hidden price-range text inputs so the form
        // submission carries the correct min/max values.
        var submenu = wrapper.closest('.mobile-facets__submenu');
        if (submenu) {
          var priceRange = submenu.querySelector('price-range');
          if (priceRange) {
            var textInputs = priceRange.querySelectorAll('input[type="text"]');
            var minVal = parseFloat(minInput.value) || 0;
            var maxVal = parseFloat(maxInput.value) || parseFloat(maxInput.max);
            // First text input = min, second = max (from price-facet.liquid structure).
            if (textInputs[0]) textInputs[0].value = minVal;
            if (textInputs[1]) textInputs[1].value = maxVal;
          }
        }

        debouncedSubmit();
      });
    });

    // Set initial UI state on bind.
    updateSliderUI(wrapper);
  }

  // Find and bind all price slider wrappers present in the DOM.
  function initSliders() {
    document.querySelectorAll('.mobile-facets__price-slider-wrapper').forEach(function (wrapper) {
      bindSlider(wrapper);
    });
  }

  // Init on DOM ready and whenever the facets are re-rendered by AJAX.
  document.addEventListener('DOMContentLoaded', initSliders);
  document.addEventListener('shopify:section:load', initSliders);
  document.addEventListener('facet:updated', initSliders);

  // Also init when a mobile filter sub-panel is opened (details toggle)
  // so the slider is ready even if it wasn't in the DOM at page load.
  document.addEventListener('click', function (e) {
    var summary = e.target.closest('.mobile-facets__summary');
    if (summary) {
      setTimeout(initSliders, 50);
    }
  });
})();

/* ============================================================
   Auto-close mobile filter drawer after a filter is selected
   (checkbox tick or price slider release). The drawer stays open
   by default in Dawn which forces users to manually tap × to see
   results. This closes it automatically after the facet form
   submits, matching the expected UX.
   ============================================================ */
(function () {
  'use strict';

  // Find the mobile filter disclosure <details> element and close it by
  // removing the menu-opening class and the open attribute — same steps
  // Dawn's MenuDrawer.closeMenuDrawer() performs.
  function closeMobileFilterDrawer() {
    var disclosure = document.querySelector('.mobile-facets__disclosure.disclosure-has-popup');
    if (!disclosure) return;

    // Remove the menu-opening class first so the CSS transition fires
    // (slide-out animation) before the open attribute is removed.
    disclosure.classList.remove('menu-opening');

    // Give the slide-out animation a moment to play (matches Dawn's default
    // transition duration of ~300ms) then remove [open] so the drawer
    // fully collapses and the summary becomes interactive again.
    setTimeout(function () {
      disclosure.removeAttribute('open');

      // Also remove overflow-hidden from body that Dawn sets when the drawer
      // is open — prevents the page from staying locked after auto-close.
      document.body.classList.remove('overflow-hidden-mobile');
    }, 300);
  }

  function debounce(fn, ms) {
    var t;
    return function () {
      clearTimeout(t);
      var a = arguments, ctx = this;
      t = setTimeout(function () { fn.apply(ctx, a); }, ms);
    };
  }

  var debouncedClose = debounce(closeMobileFilterDrawer, 900);

  // Listen for any checkbox change inside the mobile facet form.
  // Using capture phase so it fires before Dawn's own listener.
  document.addEventListener('change', function (e) {
    var checkbox = e.target.closest('.mobile-facets__checkbox, .mobile-facets__label input[type="checkbox"]');
    if (!checkbox) return;

    // Only auto-close when the checkbox is inside the mobile facet form.
    var form = checkbox.closest('#FacetFiltersFormMobile');
    if (!form) return;

    // Queue the close after the submit debounce has fired.
    debouncedClose();
  }, true);

  // Also auto-close when the price range slider handle is released
  // (the 'change' event fires on mouseup/touchend on range inputs).
  document.addEventListener('change', function (e) {
    var rangeInput = e.target.closest('.mobile-facets__range-input');
    if (!rangeInput) return;
    debouncedClose();
  }, true);

  // Also close when a facet-remove pill (active filter ×) is tapped.
  document.addEventListener('click', function (e) {
    var removeBtn = e.target.closest('.mobile-facets__submenu facet-remove a, .mobile-facets__submenu .mobile-facets__clear');
    if (!removeBtn) return;
    closeMobileFilterDrawer();
  }, true);
})();

/* ============================================================
   Filter chip × remove button
   facets.js closeFilter() already contains the correct fix:
     - Detects chips inside .mobile-facets__wrapper
     - Calls window.location.assign(removeUrl) directly (full-page nav)
   This block is intentionally left as a no-op comment so the old
   conflicting listener is gone and facets.js handles removal cleanly.
   ============================================================ */

/* Fix 25APR26 R5: reCAPTCHA badge — CSS-only hide (no JS observer).
   Root cause of the previous MutationObserver approach failing: setting inline
   visibility/width/height via setProperty created a style-attribute battle between
   our observer and Google's script — both watching for changes and re-applying their
   values, resulting in the badge sometimes staying visible.

   The fix relies entirely on the `display:none!important` rule in theme.liquid <head>.
   Stylesheet `!important` wins over any inline el.style.display='block' call from
   Google's script (non-!important inline styles lose to author stylesheet !important).
   No JS observer is needed and no conflict with Google's script can occur. */